import {CUSTOMER_SUCCESS_SEARCH_SCHEMA} from "@/components/CustomerSuccess/CustomerSuccessFilters";
import {z} from "zod";

export const CHURN_COLUMNS = [
  {key: "last_login", group: "high", format: "date"},
  {key: "last_campaign", group: "high", format: "date"},
  {key: "low_applications", group: "high", format: "percent"},
  {key: "repeat_applicants", group: "high", format: "percent"},
  {key: "extension_requests", group: "high", format: "percent"},
  {key: "left_unstarted", group: "high", format: "count"},
  {key: "ad_codes", group: "high", format: "count"},
  {key: "creator_quota", group: "medium", format: "percent"},
  {key: "buyouts", group: "medium", format: "percent"},
  {key: "stopped_rating", group: "medium", format: "count"},
  {key: "low_ratings", group: "medium", format: "rating"},
  {key: "unanswered", group: "medium", format: "count"},
  {key: "accepted_decline", group: "medium", format: "multiple"},
  {key: "cadence", group: "low", format: "multiple"},
] as const;

export const CHURN_GROUPS = ["high", "medium", "low"] as const;
export const CHURN_HEADER_INFO = {
  accepted_decline: {priority: "information"},
  ad_codes: {priority: "high"},
  agency_name: {priority: null},
  buyouts: {priority: "information"},
  cadence: {priority: "low"},
  creator_quota: {priority: "information"},
  extension_requests: {priority: "high"},
  last_campaign: {priority: "high"},
  last_login: {priority: "high"},
  left_unstarted: {priority: "high"},
  low_applications: {priority: "high"},
  low_ratings: {priority: "medium"},
  markers: {priority: null},
  renewal_date: {priority: null},
  repeat_applicants: {priority: "high"},
  stopped_rating: {priority: "medium"},
  unanswered: {priority: "medium"},
} as const;
export type TChurnHeaderKey = keyof typeof CHURN_HEADER_INFO;
export const CHURN_PILL_CLASSES = {
  high: "bg-danger-light-red text-danger-crimson",
  medium: "bg-warning-light-yellow text-warning-dark-yellow",
  low: "bg-neutral-white-dark text-neutral-black",
};
export const CHURN_DOT_CLASSES = {
  high: "bg-danger-raspberry",
  medium: "bg-warning-medium-yellow",
  low: "bg-neutral-grey-light",
};
export const CHURN_TEXT_CLASSES = {
  high: "text-danger-crimson",
  medium: "text-warning-dark-yellow",
  low: "text-neutral-grey-dark",
};
export const CHURN_FILTER_MARKERS = [
  {key: "cancellation_pending", group: "high"},
  {key: "converting", group: "high"},
  ...CHURN_COLUMNS.filter(column => !["creator_quota", "buyouts", "accepted_decline"].includes(column.key)),
] as const;

const ORDERING_KEYS: string[] = ["markers", "renewal_date", "agency_name", ...CHURN_COLUMNS.map(column => column.key)];
export const churnOrdering = (value: string | undefined) =>
  value && ORDERING_KEYS.includes(value.replace(/^-/, "")) ? value : "markers";

export const CUSTOMER_SUCCESS_VIEW_SEARCH_SCHEMA = CUSTOMER_SUCCESS_SEARCH_SCHEMA.extend({
  markers: z
    .string()
    .optional()
    .transform(
      value =>
        value
          ?.split(",")
          .filter(key => CHURN_FILTER_MARKERS.some(marker => marker.key === key))
          .join(",") || undefined,
    ),
  risk: z.enum(["act_now", "watch", "fine"]).optional().catch(undefined),
  ordering: z.string().optional(),
  view: z.enum(["overview", "churn"]).catch("overview").optional(),
}).transform(search => ({
  ...search,
  ordering:
    search.view === "churn"
      ? search.ordering === undefined
        ? undefined
        : churnOrdering(search.ordering)
      : CUSTOMER_SUCCESS_SEARCH_SCHEMA.shape.ordering.parse(search.ordering),
}));

export type TChurnInfoCard = {
  key: TChurnHeaderKey;
  status?: "agency_wide" | "information" | "never_logged_in" | "no_data" | "no_warning" | "warning";
  value?: string;
};
